import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateUserProfile, logoutUser, clearError, deleteAccount } from '../../store/slices/authSlice';
import Header from '../../layout/Header'
import MobileNav from '../../layout/MobileNav'
import ButtonLightBlue from '../../components/buttons/ButtonLightBlue';
import Input from '../../components/fields/Input'
import PageNavAccount from '../../layout/PageNavAccount';
import {SuccessMessage, ErrorMessage} from '../../components/messages/success';
import Dialog from '../../components/common/Dialog';
import ButtonRedInverted from '../../components/buttons/ButtonRedInverted';
import ButtonLightBlueInverted from '../../components/buttons/ButtonLightBlueInverted';
import {Helmet} from "react-helmet";

const AccountEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, token, isLoading, error } = useSelector((state) => state.auth);
  const [showDialog, setShowDialog] = React.useState(false);
  
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    mobile_number: '',
  });
  
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!token) {
      navigate('/login');
    } else if (user) {
      setFormData({
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        email: user.email || '',
        mobile_number: user.mobile_number || '',
      });
    }
  }, [token, user, navigate]);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  React.useEffect(() => {
    if (!token) {
      navigate('/login');
    }
  }, [token, navigate]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    dispatch(clearError());
    setSuccess('');
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    try {
      await dispatch(deleteAccount()).unwrap();
      setShowDialog(false);
    } catch (error) {
      console.error('Delete failed:', error);
      setShowDialog(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess('');

    try {
      const result = await dispatch(updateUserProfile(formData)).unwrap();
      if (result.success) {
        setSuccess('Profile updated successfully!');
      }
    } catch (err) {
      // Error is handled by Redux
    }
  };

  return(
    <>
      <Helmet>
          <title>Account Edit | Oreca Pro</title>
          <link rel="canonical" href={window.location.href} />
      </Helmet>
      <Header />
      <MobileNav />

      <Dialog
          isOpen={showDialog}
          onClose={() => setShowDialog(false)}
          onConfirm={handleDeleteAccount}
          title="Delete Spot"
          confirmText="Delete Account"
          cancelText="Cancel"
          isLoading={isLoading}
      >
          <p className="text-custom-light-grey">Are you sure you want to delete your account? You will lose access to all of your data. This action cannot be undone.</p>
      </Dialog>
      
      <div className='py-[80px] px-5 max-w-[1100px] mx-auto'>
        <h1 className='text-2xl font-semibold mb-2 md:mt-8'>Account</h1>
        <PageNavAccount />
        <form onSubmit={handleSubmit} className='max-w-[400px] mx-auto mt-10'>
          {success && (
            <SuccessMessage message={success} />
          )}
          {error && (
            <ErrorMessage message={error} />
          )}
          <h2 className="text-xl font-semibold mb-6 text-white">Edit Profile</h2>
          <Input
            type="text"
            id="firstname"
            name="firstname"
            value={formData.firstname}
            onChange={handleInputChange}
            required
            placeholder="First Name"
          />

          <Input
            type="text"
            id="lastname"
            name="lastname"
            value={formData.lastname}
            onChange={handleInputChange}
            required
            placeholder="Last Name"
          />

          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            placeholder="Email Address"
          />

          <Input
            type="tel"
            id="mobile_number"
            name="mobile_number"
            value={formData.mobile_number}
            onChange={handleInputChange}
            required
            placeholder="Mobile Number"
          />

          <div className="mt-8 flex flex-col gap-4">
            <ButtonLightBlue
              type="submit"
              disabled={isLoading}
              text={isLoading ? 'Updating...' : 'Update Profile'}
            />
          </div>

          <h2 className="text-lg font-semibold mb-6 mt-12 text-white">Logout</h2>

          <div className="mt-4 mb-12 flex flex-col gap-4">
            <ButtonLightBlueInverted 
                type="button"
                onClick={handleLogout}
                text="Logout"
            />
          </div>

          <h2 className="text-lg font-semibold mb-6 mt-12 text-white">Delete Account</h2>

          <div className="mt-4 mb-12 flex flex-col gap-4">
            <ButtonRedInverted 
                type="button"
                onClick={() => setShowDialog(true)}
                text="Delete Account"
            />
          </div>
        </form>
      </div>
    </>
  )
};

export default AccountEdit;
